//#region imports
import React, { useEffect, useCallback, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    fetchMovementReport,
    patchBooking,
    resetMovementReport,
    resetPatchBookingStatus,
    selectMovementReportData,
    selectMovementReportStatus,
    selectMovementReportError,
    selectPatchBookingStatus,
} from 'redux/slices/sla-reports/movement-report';
import { setToast, setShowToast } from '_redux/slices/timeline/timeline-toast';
import ReportHeader from './report-header';
import MovementSummary from './movement-summary';
import MovementRawDataTable from './movement-raw-data-table';
import Select from 'react-select';
import moment from 'moment';
import { Toggle, ToggleItem } from '@tremor/react';
// bootstrap
import { Col, Row } from 'react-bootstrap';

//#endregion

const MovementReport = ({ dateRange, iata, runReportRef, onRunLoadingChange, hideBreakdown }) => {

    //#region state
    const [overrideRow, setOverrideRow] = useState(null);
    const [selectedAirlines, setSelectedAirlines] = useState([]);
    const [selectedTerminal, setSelectedTerminal] = useState('all');
    const [showOnlyServed, setShowOnlyServed] = useState(false);
    //#endregion

    //#region redux
    const dispatch = useDispatch();
    const data = useSelector(selectMovementReportData);
    const status = useSelector(selectMovementReportStatus);
    const error = useSelector(selectMovementReportError);
    const patchStatus = useSelector(selectPatchBookingStatus);
    const isLoading = status === 'loading';
    const isPatching = patchStatus === 'loading';
    //#endregion

    //#region helpers
    const buildFetchParams = useCallback((airlineOverride, terminalOverride, servedOverride) => {
        const start = dateRange?.[0];
        const end = dateRange?.[1];
        if (!start || !end || !iata) return null;
        const formattedStartDate = moment(new Date(start)).format().split("T")[0];;
        const formattedEndDate = moment(new Date(end)).format().split("T")[0];
        const airlines = airlineOverride ?? selectedAirlines;
        const airlineCodes = airlines.map(s => s.value).join(',');
        const terminal = terminalOverride ?? selectedTerminal;
        const servedOnly = servedOverride ?? showOnlyServed;
        return {
            startDate: formattedStartDate,
            endDate: formattedEndDate,
            iata,
            groupByAirline: true,
            ...(airlineCodes ? { airline: airlineCodes } : {}),
            ...(iata === 'DUB' && terminal !== 'all' ? { terminal } : {}),
            ...(servedOnly ? { isServed: true } : {}),
            hideBreakdown,
        };
    }, [dateRange, iata, selectedAirlines, selectedTerminal, showOnlyServed, hideBreakdown]);
    //#endregion

    //#region derived state variables
    const dailyReports = data?.dailyReports ?? [];
    const dailyBreakdown = data?.dailyBreakdown ?? [];

    const airlineOptions = useMemo(() =>
        (data?.availableAirlines ?? []).map(a => ({ label: a.label, value: a.code })),
        [data?.availableAirlines]
    );
    //#endregion

    //#region use effects

    // run report handler
    const handleRunReport = useCallback(() => {
        const params = buildFetchParams();
        if (!params) return;
        dispatch(fetchMovementReport(params));
    }, [dispatch, buildFetchParams]);

    // airline filter change — re-fetch report with new filter immediately
    const handleAirlineFilterChange = useCallback((selection) => {
        const newSelection = selection || [];
        setSelectedAirlines(newSelection);
        const params = buildFetchParams(newSelection);
        if (!params) return;
        dispatch(fetchMovementReport(params));
    }, [dispatch, buildFetchParams]);

    // terminal filter change (DUB only) — re-fetch report with new filter immediately
    const handleTerminalFilterChange = useCallback((terminal) => {
        setSelectedTerminal(terminal);
        const params = buildFetchParams(undefined, terminal);
        if (!params) return;
        dispatch(fetchMovementReport(params));
    }, [dispatch, buildFetchParams]);

    // served-only filter change — re-fetch report with new filter immediately (server-side, reflected in summary too)
    const handleServedFilterChange = useCallback((servedOnly) => {
        setShowOnlyServed(servedOnly);
        const params = buildFetchParams(undefined, undefined, servedOnly);
        if (!params) return;
        dispatch(fetchMovementReport(params));
    }, [dispatch, buildFetchParams]);

    // on unmount: clear report
    useEffect(() => {
        return () => {
            dispatch(resetMovementReport());
        };
    }, [dispatch]);

    // listen to patch status: show loader (via patchStatus), success toast, refetch, close modal
    useEffect(() => {
        if (patchStatus === 'succeeded') {
            dispatch(setToast({ message: 'Booking updated successfully', statusType: 'success' }));
            dispatch(setShowToast(true));
            dispatch(resetPatchBookingStatus());
            setOverrideRow(null);
            const params = buildFetchParams();
            if (params) dispatch(fetchMovementReport(params));
        }
    }, [patchStatus, dispatch, buildFetchParams]);

    // listen to patch failure: show error toast
    useEffect(() => {
        if (patchStatus === 'failed') {
            dispatch(setToast({ message: 'Failed to update booking', statusType: 'failed' }));
            dispatch(setShowToast(true));
            dispatch(resetPatchBookingStatus());
        }
    }, [patchStatus, dispatch]);

    useEffect(() => {
        if (runReportRef) runReportRef.current = handleRunReport;
        return () => { if (runReportRef) runReportRef.current = null; };
    }, [handleRunReport, runReportRef]);

    useEffect(() => {
        if (onRunLoadingChange) onRunLoadingChange(isLoading);
    }, [isLoading, onRunLoadingChange]);

    //#endregion

    return (
        <div>

            {/* report header with controls */}
            <ReportHeader
                onRunReport={handleRunReport}
                isLoading={isLoading}
                hideRunButton={!!runReportRef}
            />

            {status === 'failed' && (
                <p style={{ color: '#991b1b', fontSize: '13px' }}>Error loading Movement report: {error}</p>
            )}

            {data && (
                <>
                    {/* filter controls row — terminal toggle (BFS only) beside airline dropdown */}
                    {(iata === 'DUB' || airlineOptions.length > 0) && (
                        <Row style={{ marginBottom: '16px' }}>

                            {/* terminal filter toggle — BFS only, shown once report has loaded */}
                            {iata === 'DUB' && (
                                <Col xs="auto">
                                    <div style={{ marginBottom: '5px', marginLeft: '5px' }}>
                                        <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>Filter by Terminal</span>
                                    </div>
                                    <Toggle color="zinc" value={selectedTerminal} onValueChange={handleTerminalFilterChange}>
                                        <ToggleItem value="all" text="All" />
                                        <ToggleItem value="1" text="Terminal 1" />
                                        <ToggleItem value="2" text="Terminal 2" />
                                    </Toggle>
                                </Col>
                            )}

                            {/* airline filter dropdown — shown once report has loaded */}
                            {airlineOptions.length > 0 && (
                                <Col xs={12} sm={6} md={4} style={{ maxWidth: '480px' }}>
                                    <div style={{ marginBottom: '5px', marginLeft: '5px' }}>
                                        <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>Filter by Airline</span>
                                    </div>
                                    <Select
                                        isMulti
                                        isClearable
                                        isDisabled={isLoading}
                                        options={airlineOptions}
                                        value={selectedAirlines}
                                        onChange={handleAirlineFilterChange}
                                        placeholder="All Airlines"
                                        classNamePrefix="select"
                                        styles={{
                                            control: (base) => ({ ...base, fontSize: '13px', minHeight: '36px' }),
                                            option: (base) => ({ ...base, fontSize: '13px' }),
                                            multiValue: (base) => ({ ...base, fontSize: '12px' }),
                                            placeholder: (base) => ({ ...base, fontSize: '13px', color: '#9ca3af' }),
                                        }}
                                    />
                                </Col>
                            )}
                            {/* served-only filter — re-fetches report with isServed=true, affects summary + raw data */}
                            <Col xs="auto">
                                <div style={{ marginBottom: '5px', marginLeft: '5px' }}>
                                    <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: 500 }}>Actuals</span>
                                </div>
                                <label style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    height: '36px',
                                    fontSize: '13px',
                                    color: '#374151',
                                    cursor: 'pointer',
                                }}>
                                    <input
                                        type="checkbox"
                                        checked={showOnlyServed}
                                        disabled={isLoading}
                                        onChange={(e) => handleServedFilterChange(e.target.checked)}
                                    />
                                    Served only
                                </label>
                            </Col>

                        </Row>
                    )}

                    <Row>

                        <Col xs={hideBreakdown ? 12 : 4}>

                            {/* movement summary - single aggregated report for date range */}
                            <MovementSummary dailyReports={dailyReports} dailyBreakdown={dailyBreakdown} />

                        </Col>


                        {/* raw data table - onPatchBooking dispatches patch thunk */}
                        {!hideBreakdown && (
                            <Col>
                                <MovementRawDataTable
                                    dailyReports={dailyReports}
                                    overrideRow={overrideRow}
                                    onOverrideRowChange={setOverrideRow}
                                    onPatchBooking={(row, action) => {
                                        const patchMap = {
                                            ignore: { ignore: true },
                                            duplicate: { duplicate: true },
                                        };
                                        const patch = patchMap[action] || { movementReportOveride: true };
                                        dispatch(patchBooking({
                                            bookingId: row._id,
                                            patch,
                                            reportParams: { dateRange, iata },
                                        }));
                                    }}
                                    isPatching={isPatching}
                                />
                            </Col>
                        )}

                    </Row>
                </>
            )}
        </div>
    );
};

export default MovementReport;
